import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Trash2, Recycle, Sparkles, Truck, Package, CheckCircle, ArrowRight, Leaf } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';

const T = {
  heroLabel: { de: 'Leistungen', en: 'Services' },
  heroTitle1: { de: 'Alles aus', en: 'Everything from' },
  heroTitle2: { de: 'einer Hand', en: 'One Source' },
  heroSub: { de: 'Von der Entrümpelung über die fachgerechte Entsorgung bis zur Endreinigung – wir bieten Ihnen den kompletten Service.', en: 'From clearance to professional disposal to final cleaning – we offer you the complete service.' },
  scope: { de: 'Leistungsumfang', en: 'Scope of Services' },
  enquireNow: { de: 'Jetzt anfragen', en: 'Enquire Now' },
  ctaTitle: { de: 'Kostenlose Besichtigung vereinbaren', en: 'Schedule Free Inspection' },
  ctaSub: { de: 'Wir erstellen Ihnen vor Ort ein unverbindliches Festpreis-Angebot.', en: 'We provide you with a non-binding fixed-price quote on-site.' },
  ctaBtn: { de: 'Besichtigung anfragen', en: 'Request Inspection' },
};

const SERVICES = [
  { 
    icon: Home, 
    title: 'Haushaltsauflösungen', 
    subtitle: 'Komplett & Besenrein',
    desc: 'Komplette Räumung von Wohnungen und Häusern – von der Sichtung bis zur besenreinen Übergabe.', 
    features: ['Komplette Wohnungsauflösungen', 'Nachlassräumungen mit Pietät', 'Seniorenumzüge', 'Besenreine Übergabe', 'Sichtung aller Gegenstände', 'Koordination mit Hausverwaltungen'],
    color: 'bg-gradient-to-br from-blue-500 to-blue-700'
  },
  { 
    icon: Trash2, 
    title: 'Entrümpelung', 
    subtitle: 'Privat & Gewerblich',
    desc: 'Gewerbliche und private Entrümpelung jeder Größenordnung – professionell und effizient.', 
    features: ['Kellerentrümpelung', 'Dachbodenräumung', 'Garagen & Schuppen', 'Gewerberäumungen', 'Gartenentsorgung', 'Messie-Wohnungen (diskret)'],
    color: 'bg-gradient-to-br from-red-500 to-red-700'
  },
  { 
    icon: Recycle, 
    title: 'Entsorgung & Recycling', 
    subtitle: 'Umweltbewusst',
    desc: 'Fachgerechte und umweltbewusste Entsorgung aller Materialien mit maximaler Recycling-Quote.', 
    features: ['Sperrmüllentsorgung', 'Elektroschrott (WEEE)', 'Sondermüll & Gefahrstoffe', 'Bauschuttentsorgung', '90%+ Recycling-Quote', 'Entsorgungsnachweise'],
    color: 'bg-gradient-to-br from-green-500 to-green-700'
  },
  { 
    icon: Sparkles, 
    title: 'Reinigungsservice', 
    subtitle: 'Gründlich & Sauber',
    desc: 'Professionelle Reinigung nach der Räumung – besenrein oder intensiv, wie Sie es wünschen.', 
    features: ['Grundreinigung nach Räumung', 'Baureinigung', 'Fensterreinigung', 'Desinfektionsreinigung', 'Teppichreinigung', 'Fassadenreinigung'],
    color: 'bg-gradient-to-br from-purple-500 to-purple-700'
  },
  { 
    icon: Truck, 
    title: 'Transport & Umzüge', 
    subtitle: 'Sicher & Pünktlich',
    desc: 'Sicherer Transport an den gewünschten Ort – ob Spende, Lager oder neue Adresse.', 
    features: ['Möbeltransport mit Verpackung', 'Spendenanlieferung', 'Einlagerung', 'Umzugsservice (regional)', 'Schwertransporte', 'Demontage & Montage'],
    color: 'bg-gradient-to-br from-orange-500 to-orange-700'
  },
  { 
    icon: Package, 
    title: 'Wertanrechnung', 
    subtitle: 'Fair & Transparent',
    desc: 'Verwertbare Gegenstände rechnen wir an – so reduzieren sich Ihre Kosten.', 
    features: ['Professionelle Wertschätzung', 'Faire Verrechnung', 'Spenden-Organisation', 'Verkauf verwertbarer Gegenstände', 'Transparente Kostenaufstellung', 'Detaillierte Dokumentation'],
    color: 'bg-gradient-to-br from-amber-500 to-amber-700'
  },
];

export default function AtoBObjektserviceLeistungen() {
  const { language } = useLanguage();

  return (
    <main data-testid="sub-atob-objektservice-leistungen" className="bg-[#f8f7f5]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-2 mb-6">
              <Leaf size={18} className="text-[#F5A623]" />
              <span className="text-[#F5A623] text-sm font-semibold tracking-[0.15em] uppercase">{T.heroLabel[language]}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F1A2E] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {T.heroTitle1[language]}<br />
              <span className="text-[#F5A623]">{T.heroTitle2[language]}</span>
            </h1>
            <p className="text-xl text-[#52525b] leading-relaxed">
              {T.heroSub[language]}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-[#e5e5e5] overflow-hidden group hover:shadow-2xl hover:shadow-[#0F1A2E]/5 transition-all duration-500"
              >
                {/* Header with gradient */}
                <div className={`${service.color} p-6 text-white`}>
                  <service.icon size={32} className="mb-4" />
                  <span className="text-white/70 text-xs font-semibold tracking-wider uppercase">{service.subtitle}</span>
                  <h3 className="text-xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{service.title}</h3>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <p className="text-[#52525b] mb-6 leading-relaxed">{service.desc}</p>
                  
                  <div className="space-y-3">
                    {service.features.map((feature, j) => (
                      <div key={j} className="flex items-center gap-3">
                        <CheckCircle size={16} className="text-[#F5A623] flex-shrink-0" />
                        <span className="text-[#52525b] text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">Ablauf</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F1A2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              So einfach geht's
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { num: '1', title: 'Anfrage', desc: 'Rufen Sie an oder schreiben Sie uns' },
              { num: '2', title: 'Besichtigung', desc: 'Kostenlose Vor-Ort-Begutachtung' },
              { num: '3', title: 'Festpreis', desc: 'Transparentes Angebot ohne Überraschungen' },
              { num: '4', title: 'Durchführung', desc: 'Professionelle Räumung zum Wunschtermin' },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                {i < 3 && (
                  <div className="hidden md:block absolute top-10 left-1/2 w-full h-[2px] bg-[#F5A623]/30" />
                )}
                <div className="relative bg-[#f8f7f5] rounded-2xl p-8 text-center">
                  <div className="w-14 h-14 bg-[#F5A623] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#F5A623]/30">
                    <span className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.num}</span>
                  </div>
                  <h4 className="font-semibold text-[#0F1A2E] mb-2">{step.title}</h4>
                  <p className="text-sm text-[#52525b]">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 bg-[#F5A623] rounded-full flex items-center justify-center mx-auto mb-8">
              <Home size={40} className="text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Entrümpelung nötig?
            </h2>
            <p className="text-lg text-white/70 mb-10">
              Kontaktieren Sie uns für eine kostenlose und unverbindliche Besichtigung.
            </p>
            <Link 
              to="/atob-objektservice/kontakt"
              className="inline-flex items-center gap-3 bg-[#F5A623] text-white px-10 py-5 text-sm font-bold tracking-wider uppercase hover:bg-white hover:text-[#0F1A2E] transition-all rounded-lg"
            >
              Kostenlose Besichtigung <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
